import type { ColumnDef } from "@tanstack/react-table";
import * as React from "react";
import { useAction, useMutation, useQuery } from "convex/react";
import { api } from "$convex/convexApi";
import { toast } from "sonner";
import { ChevronDown, ChevronRight, Search, ExternalLink, Copy } from "lucide-react";

import { DataTableColumnHeader } from "@/components/data-table/data-table-column-header";
import { DataTableSkeleton } from "@/components/data-table/data-table-skeleton";
import { InternalEntityDataTable } from "@/components/internal/shared/InternalEntityDataTable";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { useOrders } from "@/hooks/useOrders";
import { useAppLocale } from "@/lib/i18n";
import { formatCurrency, formatDate, formatStatusLabel as formatOrderStatusLabel } from "@/lib/format";
import {
  getEnterpriseBadgeClass,
  getEnterpriseBadgeLabel,
} from "@/lib/customer-tag-colors";
import { getStatusBadgeVariant as getBaseStatusBadgeVariant } from "@/lib/status-tag-colors";
import { useAuthIdentity } from "@/contexts/UserProfileContext";
import { m } from "@/paraglide/messages.js";
import { cn } from "@/lib/utils";

type EffectiveStatus =
  | "awaiting_customer_payment"
  | "points_applied_payment_due"
  | "installment_updated"
  | "points_credited"
  | "manual_review_required"
  | "payment_collected"
  | "payment_processing"
  | "link_expired"
  | "link_failed"
  | "resolved_no_payment";

type Direction = "increase" | "decrease" | "unchanged";

interface RichAdjustment {
  _id: string;
  orderId: string;
  orderNumber: string | null;
  customer: {
    name: string | null;
    company: string | null;
    email: string | null;
  } | null;
  orderStatus: string | null;
  paymentStatus: string | null;
  paymentMethod: string | null;
  amounts: {
    previous: number;
    new: number;
    delta: number;
    direction: Direction;
  };
  settlement: {
    resolutionMethod: string;
    effectiveStatus: EffectiveStatus;
    nextAction: string;
    statusLabel: string;
    nextActionLabel: string;
  };
  points: {
    deducted?: number;
    credited?: number;
    dollarValue?: number;
  };
  paymentLink: {
    amount?: number;
    stripeFee?: number;
    status?: string;
    url?: string;
  } | null;
  reason: string;
  adjustedBy: string;
  customerNotified?: boolean;
  notificationSentAt?: number;
  createdAt: number;
  updatedAt: number;
}

interface StatusOption {
  value: string;
  label: string;
  description: string;
}

const STATUS_OPTIONS: StatusOption[] = [
  { value: "awaiting_customer_payment", label: "Awaiting Customer Payment", description: "Customer needs to pay via payment link" },
  { value: "points_applied_payment_due", label: "Points Applied + Payment Due", description: "Points used, remainder via payment link" },
  { value: "installment_updated", label: "Installment Updated", description: "Scheduled payment amount adjusted" },
  { value: "points_credited", label: "Points Credited", description: "Customer received points credit" },
  { value: "manual_review_required", label: "Manual Review Required", description: "Needs manual resolution" },
  { value: "payment_collected", label: "Payment Collected", description: "Customer has paid" },
  { value: "payment_processing", label: "Payment Processing", description: "ACH/payment submitted and awaiting settlement" },
  { value: "link_expired", label: "Link Expired", description: "Payment link expired" },
  { value: "link_failed", label: "Link Failed", description: "Payment link creation failed" },
  { value: "resolved_no_payment", label: "Resolved - No Payment", description: "Resolved without payment needed" },
];

const MAX_MANUAL_SHIPPING_CHARGE = 1_000;

function useShippingAdjustmentsRich(status?: string, searchQuery?: string, limit = 200) {
  const { authUserId } = useAuthIdentity();
  return useQuery(
    api.shippingAdjustment.getShippingAdjustmentsRich,
    authUserId
      ? { authUserId, ...(status ? { status } : {}), ...(searchQuery ? { searchQuery } : {}), limit }
      : "skip",
  );
}

function getStatusBadgeVariant(status: EffectiveStatus): "default" | "secondary" | "destructive" | "outline" {
  switch (status) {
    case "payment_collected":
    case "resolved_no_payment":
    case "points_credited":
      return "default";
    case "awaiting_customer_payment":
    case "points_applied_payment_due":
    case "payment_processing":
      return "secondary";
    case "manual_review_required":
    case "link_expired":
    case "link_failed":
      return "destructive";
    case "installment_updated":
      return "default";
    default:
      return "outline";
  }
}

function formatStatusLabel(status: string): string {
  const found = STATUS_OPTIONS.find((s) => s.value === status);
  return found?.label || status.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());
}

function formatStatusDescription(status: string): string {
  const found = STATUS_OPTIONS.find((s) => s.value === status);
  return found?.description || "";
}

function formatNextActionLabel(action: string): string {
  const labels: Record<string, string> = {
    wait_for_customer_payment: "Wait for customer payment",
    wait_for_payment_settlement: "Wait for payment settlement",
    points_auto_applied: "Points auto-applied",
    installment_auto_updated: "Installment auto-updated",
    credit_auto_applied: "Points credit applied",
    review_manually: "Review manually",
    payment_received: "Payment received",
    resend_payment_link: "Resend payment link",
    contact_customer: "Contact customer",
    no_action_needed: "No action needed",
  };
  return labels[action] || action.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());
}

function getFundingSourceLabel(impact: any): string {
  switch (impact?.settlement?.resolutionMethod) {
    case "points_deducted":
      return "Customer points";
    case "points_partial_link":
      return "Customer points + payment link";
    case "payment_link":
      return "Payment link";
    case "scheduled_payment_updated":
      return "Pending scheduled installments";
    case "points_credit":
      return "Points credit back to customer";
    case "absorbed_under_5":
      return "Absorbed internally";
    default:
      return "Manual review";
  }
}

function getFundingBreakdownLines(impact: any): string[] {
  const settlement = impact?.settlement;
  if (!settlement) return [];

  switch (settlement.resolutionMethod) {
    case "points_deducted":
      return [
        `Deduct ${Number(settlement.pointsDeducted ?? 0).toLocaleString()} points (${formatCurrency(Number(settlement.pointsDollarValue ?? 0))}) from the customer's points balance.`,
        "No payment link will be sent.",
      ];
    case "points_partial_link":
      return [
        `Deduct ${Number(settlement.pointsDeducted ?? 0).toLocaleString()} points (${formatCurrency(Number(settlement.pointsDollarValue ?? 0))}) from the customer's points balance first.`,
        `Create a payment link for ${formatCurrency(Number(settlement.paymentLinkAmount ?? 0))}${settlement.stripeFeeAmount ? ` including ${formatCurrency(Number(settlement.stripeFeeAmount))} in fees` : ""} for the remaining amount. Staff must share that link with the customer manually.`,
      ];
    case "payment_link":
      return [
        `Create a payment link for ${formatCurrency(Number(settlement.paymentLinkAmount ?? 0))}${settlement.stripeFeeAmount ? ` including ${formatCurrency(Number(settlement.stripeFeeAmount))} in fees` : ""}. Staff must share that link with the customer manually.`,
      ];
    case "scheduled_payment_updated": {
      const delta = Number(impact?.shipping?.delta ?? 0);
      const direction = impact?.shipping?.deltaDirection;
      if (delta > 0 && direction === "increase") {
        return [
          `Add ${formatCurrency(delta)} to the next pending scheduled installment so the remaining balance stays aligned.`,
          "No separate payment link will be sent.",
        ];
      }
      if (delta > 0 && direction === "decrease") {
        return [
          `Reduce the remaining scheduled balance by ${formatCurrency(delta)} across upcoming installments as needed.`,
          "No separate payment link will be sent.",
        ];
      }
      return [
        "Update all pending scheduled installments automatically.",
        "No separate payment link will be sent.",
      ];
    }
    case "points_credit":
      return [
        `Credit ${Number(settlement.pointsCredited ?? 0).toLocaleString()} points back to the customer's points balance.`,
        "No card refund will be issued from this workflow.",
      ];
    case "absorbed_under_5":
      return ["This change is absorbed internally. Nothing will be collected from the customer."];
    default:
      return ["No automatic funding source is available for this change."];
  }
}

function getPostSaveLines(impact: any): string[] {
  const settlement = impact?.settlement;
  const sideEffects = impact?.sideEffects;
  const lines: string[] = [];

  switch (settlement?.resolutionMethod) {
    case "points_deducted":
      lines.push("Record the adjustment as resolved with no extra customer payment needed.");
      break;
    case "points_partial_link":
    case "payment_link":
      lines.push("Create the adjustment record and generate a payment link.");
      lines.push("Do not send the link automatically. Staff must copy or open it and share it with the customer.");
      lines.push("Keep the adjustment open until the payment link is paid.");
      break;
    case "scheduled_payment_updated":
      lines.push("Create the adjustment record and update all pending installments automatically.");
      break;
    case "points_credit":
      lines.push("Create the adjustment record and credit the customer back in points.");
      break;
    case "absorbed_under_5":
      lines.push("Create the adjustment record and resolve it without customer collection.");
      break;
    default:
      lines.push("Create the adjustment record and flag it for manual follow-up.");
      break;
  }

  if (sideEffects?.clearsTracking) {
    lines.push("Clear the current tracking number.");
  }
  if (sideEffects?.clearsQuote) {
    lines.push("Clear the current quote.");
  }
  if (sideEffects?.savesAddress) {
    lines.push(sideEffects?.makesAddressDefault ? "Save this address and make it the default shipping address." : "Save this address for the customer.");
  }

  return lines;
}

function getDeltaBadgeClass(direction: Direction): string {
  switch (direction) {
    case "increase":
      return "text-red-600 bg-red-50 dark:bg-red-950/30";
    case "decrease":
      return "text-green-600 bg-green-50 dark:bg-green-950/30";
    default:
      return "text-gray-600 bg-gray-50 dark:bg-gray-900";
  }
}

function AdjustmentDetailsPanel({ adjustment }: { adjustment: RichAdjustment }) {
  return (
    <div className="space-y-4 p-4 bg-muted/30 rounded-md text-sm">
      {/* Shipping Impact */}
      <div className="grid grid-cols-3 gap-4">
        <div className="space-y-1">
          <p className="text-muted-foreground">Previous Shipping</p>
          <p className="font-medium">{formatCurrency(adjustment.amounts.previous)}</p>
        </div>
        <div className="space-y-1">
          <p className="text-muted-foreground">New Shipping</p>
          <p className="font-medium">{formatCurrency(adjustment.amounts.new)}</p>
        </div>
        <div className="space-y-1">
          <p className="text-muted-foreground">Change</p>
          <p className={cn("font-medium px-2 py-0.5 rounded inline-block", getDeltaBadgeClass(adjustment.amounts.direction))}>
            {adjustment.amounts.direction === "increase" ? "+" : adjustment.amounts.direction === "decrease" ? "-" : ""}
            {formatCurrency(adjustment.amounts.delta)}
          </p>
        </div>
      </div>

      {/* Settlement Info */}
      <div className="border-t pt-4 grid grid-cols-2 gap-4">
        <div className="space-y-1">
          <p className="text-muted-foreground">Resolution Method</p>
          <p className="font-medium capitalize">{adjustment.settlement.resolutionMethod.replace(/_/g, " ")}</p>
        </div>
        <div className="space-y-1">
          <p className="text-muted-foreground">Next Action</p>
          <p className="font-medium">{adjustment.settlement.nextActionLabel}</p>
        </div>
      </div>

      {/* Points Movement */}
      {(adjustment.points.deducted || adjustment.points.credited) && (
        <div className="border-t pt-4">
          <p className="text-muted-foreground mb-2">Points</p>
          <div className="flex items-center gap-2">
            {adjustment.points.deducted && (
              <Badge variant="outline" className="text-amber-600 border-amber-200">
                {adjustment.points.deducted.toLocaleString()} pts deducted
              </Badge>
            )}
            {adjustment.points.credited && (
              <Badge variant="outline" className="text-green-600 border-green-200">
                {adjustment.points.credited.toLocaleString()} pts credited
              </Badge>
            )}
          </div>
        </div>
      )}

      {/* Payment Link Info */}
      {adjustment.paymentLink && (
        <div className="border-t pt-4">
          <p className="text-muted-foreground mb-2">Payment Link</p>
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span>Amount (with 3% fee)</span>
              <span className="font-medium">{formatCurrency(adjustment.paymentLink.amount || 0)}</span>
            </div>
            {adjustment.paymentLink.stripeFee && (
              <div className="flex items-center justify-between text-muted-foreground">
                <span>Stripe fee included</span>
                <span>{formatCurrency(adjustment.paymentLink.stripeFee)}</span>
              </div>
            )}
            {adjustment.paymentLink.status && (
              <div className="flex items-center justify-between text-muted-foreground">
                <span>Status</span>
                <Badge variant={getStatusBadgeVariant(adjustment.settlement.effectiveStatus)} className="text-xs">
                  {formatStatusLabel(adjustment.paymentLink.status)}
                </Badge>
              </div>
            )}
            <div className="flex items-center justify-between text-muted-foreground text-xs">
              <span>Customer notification</span>
              <span>
                {adjustment.customerNotified
                  ? `Emailed${adjustment.notificationSentAt ? ` ${formatDate(adjustment.notificationSentAt)}` : ""}`
                  : "Not emailed"}
              </span>
            </div>
            <p className="text-xs text-muted-foreground">
              This link is stored here for staff follow-up and customer support.
            </p>
            {adjustment.paymentLink.url && (
              <div className="flex flex-wrap items-center gap-2 pt-1">
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    void navigator.clipboard.writeText(adjustment.paymentLink?.url || "");
                    toast.success("Payment link copied.");
                  }}
                >
                  <Copy className="mr-1 h-3 w-3" />
                  Copy link
                </Button>
                <a
                  href={adjustment.paymentLink.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-primary hover:underline"
                >
                  Open payment link <ExternalLink className="h-3 w-3" />
                </a>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Reason & Metadata */}
      <div className="border-t pt-4 space-y-2">
        <div>
          <p className="text-muted-foreground">Reason</p>
          <p className="text-sm">{adjustment.reason}</p>
        </div>
        <div className="flex items-center justify-between text-muted-foreground text-xs">
          <span>Adjusted by {adjustment.adjustedBy}</span>
          <span>{formatDate(adjustment.createdAt)}</span>
        </div>
      </div>
    </div>
  );
}

function useColumns() {
  useAppLocale();
  return React.useMemo<ColumnDef<RichAdjustment>[]>(
    () => [
      {
        id: "orderNumber",
        accessorKey: "orderNumber",
        header: ({ column }) => <DataTableColumnHeader column={column} label={m.table_order_number()} />,
        cell: ({ row }) => (
          <div className="space-y-1">
            <span className="font-mono text-xs">{row.original.orderNumber || "N/A"}</span>
            {row.original.customer && (
              <div className="text-xs text-muted-foreground">
                {row.original.customer.company || row.original.customer.name || row.original.customer.email}
              </div>
            )}
          </div>
        ),
        meta: { label: m.order(), placeholder: "Search order #, customer, or company...", variant: "text" },
        enableColumnFilter: true,
      },
      {
        id: "orderStatus",
        accessorKey: "orderStatus",
        header: ({ column }) => <DataTableColumnHeader column={column} label="Order Status" />,
        cell: ({ row }) => (
          <Badge variant="outline" className="text-xs">
              {formatOrderStatusLabel(row.original.orderStatus || "")}
          </Badge>
        ),
        meta: { label: "Order Status", variant: "text" },
      },
      {
        id: "payment",
        accessorFn: (row) => ({ method: row.paymentMethod, status: row.paymentStatus }),
        header: ({ column }) => <DataTableColumnHeader column={column} label="Payment" />,
        cell: ({ row }) => (
          <div className="space-y-1">
            <Badge variant="outline" className="text-xs">
              {row.original.paymentMethod?.toUpperCase() || "N/A"}
            </Badge>
              {row.original.paymentStatus && (
              <div className="text-xs text-muted-foreground">
                {formatOrderStatusLabel(row.original.paymentStatus)}
              </div>
            )}
          </div>
        ),
        meta: { label: "Payment", variant: "text" },
      },
      {
        id: "amounts",
        accessorKey: "amounts",
        header: ({ column }) => <DataTableColumnHeader column={column} label="Shipping" />,
        cell: ({ row }) => {
          const amounts = row.original.amounts;
          return (
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                <span className="text-muted-foreground">{formatCurrency(amounts.previous)}</span>
                <ChevronRight className="h-3 w-3 text-muted-foreground" />
                <span className="font-medium">{formatCurrency(amounts.new)}</span>
              </div>
              <div className={cn("text-xs font-medium", getDeltaBadgeClass(amounts.direction))}>
                {amounts.direction === "increase" ? "▲" : amounts.direction === "decrease" ? "▼" : "•"}{" "}
                {formatCurrency(amounts.delta)}
              </div>
            </div>
          );
        },
        meta: { label: "Shipping", variant: "number" },
      },
      {
        id: "status",
        accessorKey: "settlement.effectiveStatus",
        header: ({ column }) => <DataTableColumnHeader column={column} label={m.status()} />,
        cell: ({ row }) => {
          const settlement = row.original.settlement;
          return (
            <div className="space-y-1">
              <Badge variant={getStatusBadgeVariant(settlement.effectiveStatus)} className="text-xs whitespace-nowrap">
                {settlement.statusLabel}
              </Badge>
              <div className="text-xs text-muted-foreground max-w-[200px] truncate">
                {formatStatusDescription(settlement.effectiveStatus)}
              </div>
            </div>
          );
        },
        meta: {
          label: m.status(),
          variant: "select",
          options: STATUS_OPTIONS,
        },
        enableColumnFilter: true,
      },
      {
        id: "nextAction",
        accessorKey: "settlement.nextActionLabel",
        header: ({ column }) => <DataTableColumnHeader column={column} label="Next Action" />,
        cell: ({ row }) => (
          <span className="text-xs text-muted-foreground">{row.original.settlement.nextActionLabel}</span>
        ),
        meta: { label: "Next Action", variant: "text" },
      },
      {
        id: "reason",
        accessorKey: "reason",
        header: ({ column }) => <DataTableColumnHeader column={column} label={m.reason()} />,
        cell: ({ row }) => (
          <span className="text-xs text-muted-foreground truncate max-w-[150px] block" title={row.original.reason}>
            {row.original.reason}
          </span>
        ),
        meta: { label: m.reason(), placeholder: m.search_reason_placeholder(), variant: "text" },
        enableColumnFilter: true,
      },
      {
        id: "createdAt",
        accessorKey: "createdAt",
        header: ({ column }) => <DataTableColumnHeader column={column} label={m.created()} />,
        cell: ({ row }) => <span className="text-xs">{formatDate(row.original.createdAt)}</span>,
      },
    ],
    [],
  );
}

function SearchableOrderSelect({
  orders,
  value,
  onChange,
}: {
  orders: any[];
  value: string;
  onChange: (orderId: string) => void;
}) {
  const [search, setSearch] = React.useState("");
  const [open, setOpen] = React.useState(false);
  const containerRef = React.useRef<HTMLDivElement>(null);

  // Close when clicking outside
  React.useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const filtered = React.useMemo(() => {
    if (!search.trim()) return orders.slice(0, 10);
    const q = search.toLowerCase();
    return orders
      .filter((o) =>
        (o.orderNumber?.toLowerCase() || "").includes(q) ||
        (o.user?.name?.toLowerCase() || "").includes(q) ||
        (o.user?.company?.toLowerCase() || "").includes(q) ||
        (o.user?.email?.toLowerCase() || "").includes(q),
      )
      .slice(0, 10);
  }, [orders, search]);

  const selected = orders.find((o) => o._id === value);

  return (
    <div ref={containerRef} className="relative">
      <div
        className="flex items-center justify-between w-full rounded-md border px-3 py-2 text-sm cursor-pointer hover:bg-muted/50"
        onClick={() => setOpen(!open)}
      >
        {selected ? (
          <div className="flex items-center gap-2">
            <span className="font-mono">{selected.orderNumber}</span>
            <span className="text-muted-foreground">-</span>
            <span>{selected.user?.company || selected.user?.name || selected.userId}</span>
            <Badge variant="outline" className="text-xs">
              {selected.payment?.method?.toUpperCase() || "N/A"}
            </Badge>
          </div>
        ) : (
          <span className="text-muted-foreground">Select an order...</span>
        )}
      </div>

      {open && (
        <div className="absolute z-50 mt-1 w-full rounded-md border bg-popover shadow-md">
          <div className="p-2">
            <div className="relative">
              <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search orders..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-8"
                autoFocus
              />
            </div>
          </div>
          <div className="max-h-[300px] overflow-auto">
            {filtered.length === 0 ? (
              <div className="p-2 text-sm text-muted-foreground">No orders found</div>
            ) : (
              filtered.map((order) => (
                <div
                  key={order._id}
                  className={cn(
                    "p-2 cursor-pointer hover:bg-muted flex items-center justify-between",
                    value === order._id && "bg-muted",
                  )}
                  onClick={() => {
                    onChange(order._id);
                    setOpen(false);
                  }}
                >
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-sm">{order.orderNumber}</span>
                    <Badge variant="outline" className="text-xs">
                      {formatOrderStatusLabel(order.status)}
                    </Badge>
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {order.user?.company || order.user?.name || order.user?.email || order.userId}
                    </div>
                    <div className="flex items-center gap-2 text-xs">
                      <span className="text-muted-foreground">
                        {order.payment?.method?.toUpperCase() || "N/A"}
                      </span>
                      <span className="text-muted-foreground">•</span>
                      <span className="text-muted-foreground">
                        {order.payment?.status ? formatOrderStatusLabel(order.payment.status) : "Unknown"}
                      </span>
                      <span className="text-muted-foreground">•</span>
                      <span className="text-muted-foreground">
                        Current shipping: {formatCurrency(order.delivery?.deliveryCharge || 0)}
                      </span>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default function LogisticsShippingAdjustmentsPage() {
  useAppLocale();
  const orders = useOrders(500);
  const columns = useColumns();
  const { authUserId, isPending } = useAuthIdentity();

  const [statusFilter, setStatusFilter] = React.useState<string | undefined>(undefined);
  const [searchQuery, setSearchQuery] = React.useState("");
  const [debouncedSearch, setDebouncedSearch] = React.useState("");
  const [queryLimit, setQueryLimit] = React.useState(200);

  // Debounce search
  React.useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchQuery), 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const adjustmentsResult = useShippingAdjustmentsRich(
    statusFilter,
    debouncedSearch || undefined,
    queryLimit,
  );
  const adjustments = adjustmentsResult?.adjustments || [];

  React.useEffect(() => {
    const total = adjustmentsResult?.total ?? 0;
    if (total > queryLimit) {
      setQueryLimit(total);
    }
  }, [adjustmentsResult?.total, queryLimit]);

  const adjustMutation = useMutation(api.shippingAdjustment.adjustShippingCharge);
  const createShippingAdjustmentPaymentLink = useAction(api.stripe.createShippingAdjustmentPaymentLink);

  const [createDialogOpen, setCreateDialogOpen] = React.useState(false);
  const [form, setForm] = React.useState({ orderId: "", newCharge: "", reason: "" });
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  // Fetch preview when order and valid charge are entered
  const newChargeNumber = Number.parseFloat(form.newCharge);
  const previewQuery = useQuery(
    api.shippingAdjustment.previewShippingAdjustmentImpact,
    form.orderId && Number.isFinite(newChargeNumber) && newChargeNumber >= 0
      ? {
          authUserId,
          orderId: form.orderId as any,
          newShippingCharge: newChargeNumber,
        }
      : "skip",
  );

  const activeOrders = React.useMemo(
    () => orders?.filter((o: any) => !["delivered", "cancelled", "on_hold"].includes(o.status)) ?? [],
    [orders],
  );

  const selectedOrder = React.useMemo(
    () =>
      activeOrders.find(
        (order: any) => String(order?._id ?? order?.id ?? "") === form.orderId,
      ) ?? null,
    [activeOrders, form.orderId],
  );

  const handleSubmit = React.useCallback(async () => {
    if (!form.orderId || !form.newCharge || !form.reason) {
      toast.error(m.fill_all_fields());
      return;
    }

    const newShippingCharge = Number.parseFloat(form.newCharge);
    if (!Number.isFinite(newShippingCharge)) {
      toast.error("Shipping charge must be a valid number.");
      return;
    }
    if (newShippingCharge > MAX_MANUAL_SHIPPING_CHARGE) {
      toast.error(`Shipping charge cannot exceed $${MAX_MANUAL_SHIPPING_CHARGE.toLocaleString()}.`);
      return;
    }

    setIsSubmitting(true);
    try {
      const result = await adjustMutation({
        orderId: form.orderId as any,
        newShippingCharge,
        reason: form.reason,
      });

      if (result?.adjustmentId && Number(result?.paymentLinkAmount ?? 0) > 0) {
        try {
          const linkResult = await createShippingAdjustmentPaymentLink({
            orderId: form.orderId as any,
            amount: Number(result.paymentLinkAmount),
            adjustmentId: result.adjustmentId as any,
            reason: form.reason,
          });
          if (linkResult?.success && linkResult?.paymentLinkUrl) {
            toast.warning(
              linkResult?.emailSent
                ? "Shipping adjustment created. Payment link emailed to the customer contact."
                : "Shipping adjustment created. Payment link created, but email was not sent.",
              {
                description: `${previewQuery?.impact ? getPostSaveLines(previewQuery.impact).join(" ") : ""} ${linkResult?.emailSent ? `Recipient: ${String(linkResult.recipientEmail || "customer contact")}.` : `Share this link manually. ${linkResult?.emailError ? `Email error: ${String(linkResult.emailError)}.` : ""}`} It is also saved in Shipping Adjustments. Link: ${String(linkResult.paymentLinkUrl)}`.trim(),
              },
            );
          } else {
            toast.warning("Shipping adjustment created, but payment link status is not confirmed.", {
              description: `${previewQuery?.impact ? getPostSaveLines(previewQuery.impact).join(" ") : ""} If a payment link was generated, staff must share it with the customer manually.`.trim(),
            });
          }
        } catch (linkError) {
          toast.error(
            linkError instanceof Error
              ? m.shipping_adjustment_link_failed({ message: linkError.message })
              : m.shipping_adjustment_link_generation_failed(),
          );
        }
      } else {
        toast.success("Shipping adjustment created.", {
          description: previewQuery?.impact ? getPostSaveLines(previewQuery.impact).join(" ") : undefined,
        });
      }

      setCreateDialogOpen(false);
      setForm({ orderId: "", newCharge: "", reason: "" });
    } catch (e: any) {
      toast.error(e.message || m.shipping_adjustment_create_failed());
    } finally {
      setIsSubmitting(false);
    }
  }, [form, adjustMutation, createShippingAdjustmentPaymentLink, previewQuery]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Shipping Adjustments</h1>
          <p className="text-muted-foreground mt-1">
            Manage shipping changes and view customer payment status
          </p>
        </div>
        <Button onClick={() => setCreateDialogOpen(true)}>{m.new_adjustment()}</Button>
      </div>

      {/* Search and Filter Bar */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search orders, customers, or companies..."
            value={searchQuery}
            onChange={(e) => {
              setQueryLimit(200);
              setSearchQuery(e.target.value);
            }}
            className="pl-9"
          />
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button
            key="all"
            variant={statusFilter === undefined ? "default" : "outline"}
            size="sm"
            onClick={() => {
              setQueryLimit(200);
              setStatusFilter(undefined);
            }}
          >
            All
          </Button>
          {STATUS_OPTIONS.slice(0, 5).map((opt) => (
            <Button
              key={opt.value}
              variant={statusFilter === opt.value ? "default" : "outline"}
              size="sm"
              onClick={() => {
                setQueryLimit(200);
                setStatusFilter(opt.value);
              }}
              className="whitespace-nowrap"
            >
              {opt.label}
            </Button>
          ))}
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>{m.adjustments()}</CardTitle>
          <CardDescription>
            {adjustmentsResult?.total ?? 0} adjustments found
            {debouncedSearch && ` matching "${debouncedSearch}"`}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {adjustmentsResult === undefined || (isPending && adjustments.length === 0) ? (
            <DataTableSkeleton columnCount={columns.length} rowCount={10} withPagination />
          ) : (
            <InternalEntityDataTable
              data={adjustments.map((a: RichAdjustment) => ({ ...a, id: a._id }))}
              columns={columns}
              initialSorting={[{ id: "createdAt", desc: true }]}
              enableSelection={false}
              expandedRow={{
                getCanExpand: () => true,
                render: ({ row }) => <AdjustmentDetailsPanel adjustment={row.original} />,
              }}
            />
          )}
        </CardContent>
      </Card>

      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="max-h-[calc(100dvh-2rem)] grid-rows-[auto_minmax(0,1fr)_auto] sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>{m.create_shipping_adjustment()}</DialogTitle>
            <DialogDescription>
              Select an order and enter the new shipping charge. Review the preview below before confirming.
            </DialogDescription>
          </DialogHeader>
          <div className="grid min-h-0 gap-4 overflow-y-auto py-4 pr-1">
            <div className="grid gap-2">
              <Label>{m.order()}</Label>
              <SearchableOrderSelect
                orders={activeOrders}
                value={form.orderId}
                onChange={(v) => setForm((p) => ({ ...p, orderId: v }))}
              />
            </div>
            <div className="grid gap-2">
              <Label>{m.new_shipping_charge_label()}</Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                max={MAX_MANUAL_SHIPPING_CHARGE}
                value={form.newCharge}
                onChange={(e) => setForm((p) => ({ ...p, newCharge: e.target.value }))}
              />
              <p className="text-xs text-muted-foreground">
                Manual adjustments are capped at ${MAX_MANUAL_SHIPPING_CHARGE.toLocaleString()}. For larger amounts, contact support.
              </p>
            </div>

            {/* Preview Section */}
            {previewQuery?.success && previewQuery.impact && (
              <div className="rounded-md border bg-muted/50 p-4 space-y-3">
                <div className="space-y-1">
                  <div className="font-medium text-sm">Preview: What will happen</div>
                  <p className="text-xs text-muted-foreground">
                    {selectedOrder?.paymentMethod ? `${String(selectedOrder.paymentMethod).toUpperCase()} order` : "Order"}
                    {selectedOrder?.paymentStatus ? `, currently ${formatOrderStatusLabel(String(selectedOrder.paymentStatus))}.` : "."}
                  </p>
                </div>

                <div className="flex flex-wrap items-center gap-2 text-sm">
                  <span className="text-muted-foreground">Shipping:</span>
                  <span className="font-mono">{formatCurrency(Number(previewQuery.impact.shipping.previousCharge ?? 0))}</span>
                  <span className="text-muted-foreground">→</span>
                  <span className="font-mono font-medium">{formatCurrency(Number(previewQuery.impact.shipping.newCharge ?? 0))}</span>
                  {previewQuery.impact.shipping.deltaDirection === "increase" ? (
                    <Badge variant="destructive" className="text-xs">+{formatCurrency(Number(previewQuery.impact.shipping.delta ?? 0))}</Badge>
                  ) : previewQuery.impact.shipping.deltaDirection === "decrease" ? (
                    <Badge variant="default" className="bg-green-600 text-xs">-{formatCurrency(Number(previewQuery.impact.shipping.delta ?? 0))}</Badge>
                  ) : null}
                </div>

                <div className="rounded-md border bg-background p-3 space-y-2">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm font-medium">Where this will come from</span>
                    <Badge variant="outline">{getFundingSourceLabel(previewQuery.impact)}</Badge>
                  </div>
                  <div className="space-y-1 text-sm text-muted-foreground">
                    {getFundingBreakdownLines(previewQuery.impact).map((line) => (
                      <p key={line}>• {line}</p>
                    ))}
                  </div>
                </div>

                <div className="rounded-md border bg-background p-3 space-y-2">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm font-medium">What we will do after save</span>
                    <Badge variant={getStatusBadgeVariant(previewQuery.impact.settlement.effectiveStatus)}>
                      {previewQuery.impact.settlement.statusLabel}
                    </Badge>
                  </div>
                  <div className="space-y-1 text-sm text-muted-foreground">
                    {getPostSaveLines(previewQuery.impact).map((line) => (
                      <p key={line}>• {line}</p>
                    ))}
                  </div>
                  <p className="text-xs text-muted-foreground pt-2 border-t">
                    Next action: {previewQuery.impact.settlement.nextActionLabel}
                  </p>
                </div>

                {previewQuery.impact.warnings?.length ? (
                  <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
                    <div className="font-medium">Heads up</div>
                    <div className="mt-1 space-y-1">
                      {previewQuery.impact.warnings.map((warning: string) => (
                        <p key={warning}>• {warning}</p>
                      ))}
                    </div>
                  </div>
                ) : null}

                {previewQuery.impact.blockingIssues?.length ? (
                  <div className="rounded-md border border-destructive bg-destructive/10 p-3 text-sm text-destructive">
                    <div className="font-medium">Cannot apply yet</div>
                    <div className="mt-1 space-y-1">
                      {previewQuery.impact.blockingIssues.map((issue: string) => (
                        <p key={issue}>• {issue}</p>
                      ))}
                    </div>
                  </div>
                ) : null}
              </div>
            )}

            {/* Error State */}
            {previewQuery && !previewQuery.success && (
              <div className="rounded-md border border-destructive bg-destructive/10 p-3 text-sm text-destructive">
                {previewQuery.error || "Unable to calculate adjustment impact"}
              </div>
            )}

            <div className="grid gap-2">
              <Label>{m.reason()}</Label>
              <Textarea
                placeholder="Why is this shipping adjustment needed?"
                value={form.reason}
                onChange={(e) => setForm((p) => ({ ...p, reason: e.target.value }))}
              />
            </div>
          </div>
          <DialogFooter className="border-t pt-4">
            <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>
              {m.cancel()}
            </Button>
            <Button
              onClick={() => void handleSubmit()}
              disabled={
                isSubmitting
                || !previewQuery?.success
                || Boolean(previewQuery?.impact?.blockingIssues?.length)
              }
            >
              {isSubmitting ? m.creating() : m.create_adjustment()}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
